import React, { useMemo, useRef, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { notificationHistoryService, notificationSettingsService } from '@services';
import ListPageHeader from '@components/common/ListPageHeader';
import { RZTable } from '@components/common/table/RZTable';
import ErrorState from '@components/common/ErrorState';
import Badge from '@components/common/Badge';
import Select from '@components/common/Select';
import DateInput from '@components/common/DateInput';
import Tooltip from '@components/common/Tooltip';
import { formatShortDate } from '@utils/dateUtils';
import { capitalize } from '@utils/stringUtils';
import { notificationHistoryStatusToVariant } from '@utils/badgeMappers';
import { useDebounce } from '@hooks/useDebounce';
import { usePagination } from '@hooks/usePagination';
import type {
  NotificationHistoryFilters,
  NotificationHistoryRow,
  TableColumn
} from '@app-types';
import styles from './NotificationHistoryTab.module.css';

// Matches the debounce used by the other admin list pages (e.g. ContractsPage).
const SEARCH_DEBOUNCE_MS = 500;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' }
];

const columns: TableColumn[] = [
  { key: 'assessment', label: 'Assessment', defaultVisible: true },
  { key: 'supplier_contract', label: 'Supplier · Contract', defaultVisible: true },
  { key: 'type', label: 'Type', defaultVisible: true },
  { key: 'recipients', label: 'Recipients (To · CC)', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true, centered: true },
  { key: 'sent_at', label: 'Sent', defaultVisible: true },
  { key: 'attempts', label: 'Attempts', defaultVisible: true, centered: true },
  { key: 'last_error', label: 'Error', defaultVisible: true }
];

// `recipients` (and its to/cc arrays) come from the outbox API and may be null
// for rows that never resolved recipients (e.g. skipped/pending), so guard them.
function recipientSummary(recipients: NotificationHistoryRow['recipients'] | null | undefined): string {
  const fmt = (list: { name: string; email: string }[] = []) =>
    list.map((r) => r.name || r.email).join(', ');
  const to = fmt(recipients?.to);
  const cc = fmt(recipients?.cc);
  if (cc) {
    // Avoid a dangling "· cc: …" prefix for CC-only rows where `to` is empty.
    return to ? `${to} · cc: ${cc}` : `cc: ${cc}`;
  }
  return to;
}

// Full recipient list for the tooltip, one address per line and grouped by
// To/CC so the complete list stays readable even when the cell text is truncated.
function recipientDetails(recipients: NotificationHistoryRow['recipients'] | null | undefined): React.ReactNode {
  const format = (list: { name: string; email: string }[] = []) =>
    list.map((r) => (r.name ? `${r.name} <${r.email}>` : r.email));
  const to = format(recipients?.to);
  const cc = format(recipients?.cc);

  return (
    <div className={styles.recipientDetails}>
      {to.length > 0 && (
        <div>
          <strong>To:</strong>
          {to.map((entry) => (
            <div key={entry}>{entry}</div>
          ))}
        </div>
      )}
      {cc.length > 0 && (
        <div>
          <strong>CC:</strong>
          {cc.map((entry) => (
            <div key={entry}>{entry}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// A native <input type="date"> can emit a 5-digit year (e.g. "20260-07-02"), which
// the backend rejects with a 400 the UI can't act on. Bound the accepted range and
// require a 4-digit-year YYYY-MM-DD shape so invalid dates are caught client-side.
const MIN_DATE = '1900-01-01';
const MAX_DATE = '2100-12-31';
const isValidDate = (value: string): boolean =>
  value === '' || (/^\d{4}-\d{2}-\d{2}$/.test(value) && value >= MIN_DATE && value <= MAX_DATE);

// Extracted so the "To" field's error derivation below stays a flat if/return
// rather than a nested ternary (invalid value takes precedence over an inverted range).
function toDateError(dateFrom: string, dateTo: string): string {
  if (!isValidDate(dateTo)) return 'Please enter a valid date';
  if (isValidDate(dateFrom) && dateFrom && dateTo && dateFrom > dateTo) {
    return 'From date must be on or before To date';
  }
  return '';
}

export interface NotificationHistoryTabProps {
  // Gates the history/catalog queries so data is fetched only once the tab is active.
  enabled: boolean;
}

const NotificationHistoryTab: React.FC<NotificationHistoryTabProps> = ({ enabled }) => {
  const { currentPage, pageSize, setCurrentPage, setPageSize } = usePagination({
    storageKey: 'notification-history-page-size',
    defaultPageSize: 25
  });

  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Debounce the free-text search so we don't refetch on every keystroke.
  const debouncedSearch = useDebounce(searchInput.trim(), SEARCH_DEBOUNCE_MS);

  const fromError = !isValidDate(dateFrom) ? 'Please enter a valid date' : '';
  const toError = toDateError(dateFrom, dateTo);
  // Any date error (invalid value OR inverted range) blocks applying that value below.
  const dateError = fromError || toError;

  // While the typed range is inverted, keep querying by the last valid bounds rather
  // than dropping date filtering entirely (which would refetch the full history and
  // also discard a still-valid lower bound).
  const lastValidDates = useRef({ from: '', to: '' });
  if (!dateError) {
    lastValidDates.current = { from: dateFrom, to: dateTo };
  }
  const { from: appliedDateFrom, to: appliedDateTo } = lastValidDates.current;

  // Whether the user has narrowed the list (drives the empty-state copy).
  const hasActiveFilters = Boolean(statusFilter || typeFilter || debouncedSearch || dateFrom || dateTo);

  // Type dropdown is populated from the dispatchable notification types (code →
  // friendly name), not the full settings catalog — that also includes config-only
  // entries like Delivery Settings, which are never sent and never appear in history.
  // Gated on `enabled` so it fires only when the History tab is active.
  const { data: catalog } = useQuery({
    queryKey: ['notification-types'],
    queryFn: () => notificationSettingsService.getNotificationTypes(),
    enabled
  });

  const typeOptions = useMemo(
    () => [
      { value: '', label: 'All types' },
      ...(catalog ?? []).map((n) => ({ value: n.code, label: n.name }))
    ],
    [catalog]
  );

  const filters: NotificationHistoryFilters = useMemo(
    () => ({
      status: statusFilter || undefined,
      notification_type: typeFilter || undefined,
      search: debouncedSearch || undefined,
      date_from: appliedDateFrom || undefined,
      date_to: appliedDateTo || undefined
    }),
    [statusFilter, typeFilter, debouncedSearch, appliedDateFrom, appliedDateTo]
  );

  // Reset to the first page whenever the active filters change. Done during render
  // (React's "adjust state while rendering" pattern) instead of in an effect, so the
  // stale page never reaches the data query and it is immune to StrictMode's
  // double-invoked effects.
  const filtersKey = `${statusFilter}|${typeFilter}|${debouncedSearch}|${appliedDateFrom}|${appliedDateTo}`;
  const prevFiltersKey = useRef<string | null>(null);
  if (prevFiltersKey.current === null) {
    prevFiltersKey.current = filtersKey;
  } else if (prevFiltersKey.current !== filtersKey) {
    prevFiltersKey.current = filtersKey;
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['notification-history', filters, currentPage, pageSize],
    queryFn: () => notificationHistoryService.getHistory(filters, currentPage, pageSize),
    placeholderData: keepPreviousData,
    enabled
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const renderCell = (column: TableColumn, row: NotificationHistoryRow): React.ReactNode => {
    switch (column.key) {
      case 'assessment':
        if (!row.assessment_id) return '—';
        return row.assessment_name || `Assessment #${row.assessment_id}`;
      case 'supplier_contract':
        return [row.supplier_name, row.contract_number].filter(Boolean).join(' · ') || '—';
      case 'type':
        return row.notification_name || row.notification_type;
      case 'recipients': {
        const summary = recipientSummary(row.recipients);
        if (!summary) return '—';
        const hasRecipients = Boolean(row.recipients?.to?.length || row.recipients?.cc?.length);
        return (
          <Tooltip
            content={recipientDetails(row.recipients)}
            position="top"
            width={300}
            disabled={!hasRecipients}
          >
            <span className={styles.recipients}>{summary}</span>
          </Tooltip>
        );
      }
      case 'status':
        if (!row.status) return '—';
        return (
          <Badge variant={notificationHistoryStatusToVariant(row.status)} dataTestId="notification-status">
            {capitalize(row.status)}
          </Badge>
        );
      case 'sent_at':
        return row.sent_at ? formatShortDate(row.sent_at) : '—';
      case 'attempts':
        return row.attempts;
      case 'last_error':
        if (!row.last_error) return '—';
        return (
          <Tooltip content={row.last_error} position="top">
            <span className={styles.errorCell}>{row.last_error}</span>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.historyTab} data-test-id="notification-history-tab">
      <ListPageHeader
        icon={<History size={24} />}
        title="Notification History"
        count={total}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search assessment, contract, supplier..."
      />

      <div className={styles.filters} data-test-id="notification-history-filters">
        <Select
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="All statuses"
          wrapperClassName={styles.filter}
          dataTestId="status-filter"
        />
        <Select
          options={typeOptions}
          value={typeFilter}
          onChange={setTypeFilter}
          placeholder="All types"
          wrapperClassName={styles.filter}
          dataTestId="type-filter"
        />
        <DateInput
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          min={MIN_DATE}
          max={MAX_DATE}
          error={fromError || undefined}
          wrapperClassName={styles.filter}
          dataTestId="date-from"
          aria-label="From date"
        />
        <DateInput
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          min={MIN_DATE}
          max={MAX_DATE}
          error={toError || undefined}
          wrapperClassName={styles.filter}
          dataTestId="date-to"
          aria-label="To date"
        />
      </div>

      {error ? (
        <ErrorState
          message="Failed to load notification history. Please try again."
          data-test-id="notification-history-error"
        />
      ) : (
        <RZTable<NotificationHistoryRow>
          data={rows}
          columns={columns}
          getRowKey={(row) => row.id}
          renderCell={renderCell}
          pagination={{
            currentPage,
            totalPages,
            totalItems: total,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
            itemName: 'notifications',
            pageSizeOptions: [25, 50, 100]
          }}
          state={{
            isLoading,
            isFetching,
            isEmpty: rows.length === 0,
            loadingMessage: 'Loading notification history...'
          }}
          emptyState={
            <RZTable.Empty
              icon={<History size={48} />}
              title="No notifications found"
              description={
                hasActiveFilters
                  ? 'No notifications match your current filters. Try adjusting or clearing them.'
                  : 'Notifications appear here once assessments are submitted, closed, or reminders run.'
              }
            />
          }
        />
      )}
    </div>
  );
};

export default NotificationHistoryTab;
